import React, { useState, useEffect, useMemo } from 'react';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import ClientMattersService from '@frontend/AppSettings/services/ClientMattersService';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import './SummaryDetails.css';
import SummaryWidgets from './SummaryWidgets';
import CoreRelationshipCard from './cards/CoreRelationshipCard';
import ContactInfoCard from './cards/ContactInfoCard';
import KeyContactsCard from './cards/KeyContactsCard';
import NotesTagsCard from './cards/NotesTagsCard';

interface SummaryDetailsProps {
  stakeholderId: string;
  type: 'individual' | 'organization';
}

const SummaryDetails: React.FC<SummaryDetailsProps> = ({ stakeholderId, type }) => {
  const [stakeholderData, setStakeholderData] = useState<any>(null);
  const [matters, setMatters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let data;
        if (type === 'individual') {
          const clients = await ClientService.getClients();
          data = clients.find((c: any) => String(c.ClientID) === stakeholderId);
          if (!data) {
            const agents = await AgentsService.getAgents();
            data = agents.find((a: any) => String(a.AgentID) === stakeholderId);
          }
        } else {
          // For organizations, the stakeholderId is in format "org-{ClientID}" or "org-{AgentID}"
          // Extract the actual ID and find the organization
          const actualId = stakeholderId.startsWith('org-') 
            ? stakeholderId.replace('org-', '') 
            : stakeholderId;
          
          const clients = await ClientService.getClients();
          const agents = await AgentsService.getAgents();
          
          // Try to find by ClientID first
          let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
          if (foundClient && foundClient.Company) {
            // Found a client with this ID, use it as the base
            data = foundClient;
          } else {
            // Try to find by AgentID
            let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
            if (foundAgent && foundAgent.Company) {
              // Found an agent with this ID, use it as the base
              data = foundAgent;
            } else {
              // Fallback: try to find by Company name directly
              data = clients.find((c: any) => c.Company === stakeholderId || c.Company === actualId);
            }
          }
        }

        if (data) {
          setStakeholderData(data);
          
          // Fetch matters using the same logic as StakeholderMatters
          let allMatters: any[] = [];

          if (type === 'organization') {
            // For organizations, extract the actual ID and find all clients/agents with the same company
            const actualId = stakeholderId.startsWith('org-') 
              ? stakeholderId.replace('org-', '') 
              : stakeholderId;
            
            const clients = await ClientService.getClients();
            const agents = await AgentsService.getAgents();
            
            // Find the organization's company name
            let companyName: string | null = null;
            let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
            if (foundClient && foundClient.Company) {
              companyName = foundClient.Company;
            } else {
              let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
              if (foundAgent && foundAgent.Company) {
                companyName = foundAgent.Company;
              }
            }

            if (companyName) {
              // Get all client IDs with this company name (as strings for API)
              const orgClientIds = clients
                .filter((c: any) => c.Company === companyName)
                .map((c: any) => String(c.ClientID));
              
              // Fetch matters for all clients in the organization
              const mattersPromises = orgClientIds.map(async (clientId: string) => {
                try {
                  return await ClientMattersService.getClientMatters(clientId);
                } catch (error) {
                  console.error(`Error fetching matters for client ${clientId}:`, error);
                  return [];
                }
              });
              
              const mattersArrays = await Promise.all(mattersPromises);
              allMatters = mattersArrays.flat();
            }
          } else {
            // For individuals, use the stakeholderId directly
            const responseData = await ClientMattersService.getClientMatters(stakeholderId);
            allMatters = Array.isArray(responseData) ? responseData : [];
          }

          // Ensure allMatters is an array before processing
          if (!Array.isArray(allMatters)) {
            allMatters = [];
          }

          // Remove duplicates and sort (same as StakeholderMatters)
          const uniqueMatters = Array.from(
            new Map(allMatters.map((m: any) => [m.matterid, m])).values()
          );
          
          const sortedData = uniqueMatters.sort((a: any, b: any) => {
            const numA = parseInt(a.matterid?.split('-').pop() || '0', 10);
            const numB = parseInt(b.matterid?.split('-').pop() || '0', 10);
            return numA - numB;
          });
          setMatters(sortedData);
        }
      } catch (error) {
        console.error('Error fetching stakeholder data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [stakeholderId, type]);

  // Calculate matters counts using the same logic as StakeholderMatters
  // Hooks must be called before early returns
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isCompleted = (matter: any) =>
    matter.status && (matter.status.toLowerCase().includes('complete') || matter.status.toLowerCase().includes('closed'));

  const mattersSummary = useMemo(() => {
    if (!matters || matters.length === 0) {
      return { total: 0, open: 0 };
    }

    return matters.reduce(
      (acc, matter) => {
        const completed = isCompleted(matter);

        let dueDate: Date | null = null;
        const rawDue = matter.closedate || matter.due_date;
        if (rawDue) {
          const parsed = Date.parse(rawDue);
          if (!Number.isNaN(parsed)) {
            dueDate = new Date(parsed);
            dueDate.setHours(0, 0, 0, 0);
          }
        }

        // Open matters are those that are not completed (includes both in progress and overdue)
        if (!completed) {
          acc.open += 1;
        }

        acc.total += 1;
        return acc;
      },
      { total: 0, open: 0 }
    );
  }, [matters, today]);

  const openMatters = mattersSummary.open;
  const totalMatters = mattersSummary.total;

  if (loading) {
    return <div className="stakeholder-details-loading">Loading...</div>;
  }

  if (!stakeholderData) {
    return <div className="stakeholder-details-error">Stakeholder not found</div>;
  }

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return 'N/A';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="stakeholder-summary-details">
      <SummaryWidgets
        type={type}
        stakeholderData={stakeholderData}
        openMatters={openMatters}
        totalMatters={totalMatters}
        formatDate={formatDate}
        useProjectsLabel={isEngineeringEnvironment}
      />

      {/* Main two-column layout */}
      <div className="stakeholder-main-grid">
        <div className="stakeholder-main-left">
          <CoreRelationshipCard
            stakeholderData={stakeholderData}
            type={type}
            formatDate={formatDate}
            getInitials={getInitials}
            stakeholderId={stakeholderId}
          />
        </div>

        {/* Contact Information */}
        <ContactInfoCard stakeholderData={stakeholderData} />
      </div>

      {/* Lower layout - two columns for organizations, single column for individuals */}
      <div className={`stakeholder-bottom-grid ${type === 'individual' ? 'stakeholder-bottom-grid-single' : ''}`}>
        {type === 'organization' && (
          <KeyContactsCard 
            getInitials={getInitials}
            stakeholderId={stakeholderId}
            type={type}
            stakeholderData={stakeholderData}
          />
        )}
        <NotesTagsCard 
          stakeholderId={stakeholderId}
          type={type}
          stakeholderData={stakeholderData}
        />
      </div>
    </div>
  );
};

export default SummaryDetails;

